// Noyau — FEUILLES « TRACER UN PROGRAMME PYTHON » au format publié (plugin « python »).
//
// Le juge est celui de l'ancien moteur (`packages/reasoner/src/kernel-python.ts` et son interpréteur sûr
// `kernel/python/`, lots 5.7, 5.7 bis et 6.56), transcrit en JavaScript sans changement dans
// `./python/kernel-python.js` (un seul fichier, produit par esbuild). Objectifs `trace` (l'élève écrit l'état du
// programme tour par tour : « i = 2 ; s = 3 », un test « u ≥ 100 », un appel « f(1) = −2 ») et `complete` (il
// écrit la ligne de code manquante, le programme complété est exécuté) ; mêmes messages, mêmes HORA.
//
// Le juge de la feuille (judge.js) appelle `createPythonSheet` puis `judgePythonLine` ; l'ancien juge ne modifie
// son état que dans `kprogCommit` : on copie ces champs-là avant l'enregistrement et on range la copie dans la
// ligne gardée (`.prog`), la feuille reste immuable.
// ============================================================================

import {
  createKProgState,
  kprogCodeAtoms,
  kprogCommit,
  kprogGoalReached,
  kprogIsAnswer,
  kprogJudge,
  kprogLineAtoms,
} from './python/kernel-python.js';

export function createPythonSheet(model) {
  return createKProgState(model);
}

/** Le texte de départ affiché : l'appel suivi, ou la variable cherchée. */
export function pythonStartText(model) {
  return String(model.kernel.start ?? '');
}

// les seuls champs que `kprogCommit` modifie
const clone = (ks) => ({
  ...ks,
  lines: [...ks.lines],
  visited: [...ks.visited],
  condsDone: new Set(ks.condsDone),
  callsDone: new Set(ks.callsDone),
  evalsDone: new Set(ks.evalsDone),
  atLast: new Set(ks.atLast),
});

const atomsOf = (ks, text) =>
  ks.mode === 'complete' ? kprogCodeAtoms(String(text)) : kprogLineAtoms(String(text));

/**
 * Une proposition du QCM est-elle trouvée ? Seulement la réponse, une fois la trace finie (`kprogIsAnswer`) :
 * un état intermédiaire (« n = 2 » avant « n = 8 ») ne coche jamais une proposition.
 */
export function pythonHasAnswer(ks, text) {
  try {
    return kprogIsAnswer(ks, String(text));
  } catch {
    return false;
  }
}

export function judgePythonLine(ks, text) {
  let v;
  try {
    v = kprogJudge(ks, [{ atoms: atomsOf(ks, text) }], { verify: true });
  } catch (e) {
    return {
      title: null,
      status: 'parse_error',
      kind: null,
      message: e instanceof Error ? e.message : String(e),
    };
  }
  const base = { title: v.title ?? null, message: v.message ?? null };
  if (v.kind === 'step' || v.kind === 'ready') {
    const next = clone(ks);
    kprogCommit(next, v);
    const done = kprogGoalReached(next);
    const rule = v.rule;
    const gestures = rule
      ? [{ ruleId: rule.id, label: rule.label, why: rule.why, hora: rule.hora ?? 0 }]
      : [];
    return {
      ...base,
      status: done ? 'goal' : 'step',
      kind: 'step',
      gestures,
      hora: v.hora ?? rule?.hora ?? 0,
      message:
        v.message ?? (done ? 'Tu tiens la réponse !' : rule ? `Juste : ${rule.label}.` : 'Juste.'),
      prog: next,
    };
  }
  if (v.kind === 'useless') {
    const next = v.pmove ? clone(ks) : null;
    if (next) kprogCommit(next, v);
    return {
      ...base,
      status: 'useless',
      kind: 'kept',
      gestures: [],
      hora: 0,
      ...(next ? { prog: next } : {}),
    };
  }
  if (v.kind === 'unjustified')
    return { ...base, status: 'unjustified', kind: 'kept', gestures: [], hora: 0 };
  if (v.kind === 'false' || v.kind === 'trap')
    return { ...base, status: v.kind === 'trap' ? 'trap' : 'wrong', kind: null };
  return {
    ...base,
    status: 'parse_error',
    kind: null,
    message: v.message ?? 'Termine ta ligne.',
  };
}
